import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ["Kitchen", "Bathroom", "Addition", "Whole Home", "Other"];
const BUDGETS = ["Under $50k", "$50k – $120k", "$120k – $300k", "$300k+"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#F4F3F0]/50">
      {label}
    </span>
    <div className="mt-3">{children}</div>
  </label>
);

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 py-3 text-lg font-light text-[#F4F3F0] placeholder:text-[#F4F3F0]/30 focus:outline-none focus:border-[#C05C3F] transition-colors duration-300";

export const Quote = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "Kitchen",
    budget: BUDGETS[1],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const handler = (e) => {
      const pt = e.detail?.project_type;
      if (pt && PROJECT_TYPES.includes(pt)) {
        setForm((f) => ({ ...f, project_type: pt }));
      }
    };
    window.addEventListener("prefill-quote", handler);
    return () => window.removeEventListener("prefill-quote", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short brief.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Received. We'll be in touch within one business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        project_type: "Kitchen",
        budget: BUDGETS[1],
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="border-t border-white/10 bg-[#0d0d0c]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#C05C3F]">
            / Start here
          </span>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#F4F3F0] leading-[0.95]">
            Tell us about <span className="italic">your home.</span>
          </h2>
          <p className="mt-6 max-w-sm text-base text-[#F4F3F0]/70 font-light leading-relaxed">
            Share a few details and we'll come back with a considered response —
            no call centres, no automated quotes. Just a builder who reads it.
          </p>
          <div className="mt-10 space-y-5 text-[#F4F3F0]/60">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C05C3F]">Residential Renovations</div>
              <p className="mt-1 font-mono text-sm tracking-[0.1em] text-[#F4F3F0]/85">905 805 7681</p>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C05C3F]">Commercial Renovations</div>
              <p className="mt-1 font-mono text-sm tracking-[0.1em] text-[#F4F3F0]/85">289 941 4926</p>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C05C3F]">Subcontracting</div>
              <p className="mt-1 font-mono text-sm tracking-[0.1em] text-[#F4F3F0]/85">905 514 9025</p>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C05C3F]">Studio</div>
              <p className="mt-1 font-mono text-sm tracking-[0.1em] text-[#F4F3F0]/85">hello@greenstarrenos.com</p>
              <p className="mt-1 text-sm font-light leading-relaxed text-[#F4F3F0]/70 not-italic">
                1201 – 1200 Bay Street, Toronto, ON, M54 2A5
              </p>
            </div>
          </div>
        </div>

        <Reveal className="lg:col-span-7" y={30}>
          <form onSubmit={submit} data-testid="quote-form" className="space-y-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <Field label="Full name">
                <input
                  data-testid="quote-name"
                  className={inputCls}
                  placeholder="Jane Doe"
                  value={form.name}
                  onChange={set("name")}
                />
              </Field>
              <Field label="Email">
                <input
                  data-testid="quote-email"
                  type="email"
                  className={inputCls}
                  placeholder="jane@email.com"
                  value={form.email}
                  onChange={set("email")}
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              <Field label="Phone (optional)">
                <input
                  data-testid="quote-phone"
                  className={inputCls}
                  placeholder="+1 ..."
                  value={form.phone}
                  onChange={set("phone")}
                />
              </Field>
              <Field label="Project type">
                <select
                  data-testid="quote-project-type"
                  className={`${inputCls} appearance-none cursor-pointer`}
                  value={form.project_type}
                  onChange={set("project_type")}
                >
                  {PROJECT_TYPES.map((p) => (
                    <option key={p} value={p} className="bg-[#1C1C1A]">
                      {p}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Budget range">
              <div className="flex flex-wrap gap-3 mt-1">
                {BUDGETS.map((b) => (
                  <button
                    type="button"
                    key={b}
                    data-testid={`quote-budget-${b}`}
                    onClick={() => setForm((f) => ({ ...f, budget: b }))}
                    className={`font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors duration-300 ${
                      form.budget === b
                        ? "bg-[#C05C3F] border-[#C05C3F] text-[#F4F3F0]"
                        : "border-white/20 text-[#F4F3F0]/60 hover:border-[#C05C3F]"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Your brief">
              <textarea
                data-testid="quote-message"
                rows={4}
                className={`${inputCls} resize-none`}
                placeholder="Tell us what you're dreaming of..."
                value={form.message}
                onChange={set("message")}
              />
            </Field>

            <button
              type="submit"
              data-testid="quote-submit"
              disabled={loading}
              className="group font-mono text-xs uppercase tracking-[0.2em] bg-[#C05C3F] text-[#F4F3F0] px-10 py-5 hover:bg-[#F4F3F0] hover:text-[#111110] transition-colors duration-300 flex items-center gap-3 disabled:opacity-50"
            >
              {loading ? "Sending..." : "Send enquiry"}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
